/**
 * Hook for pre-snap motion management in canvas
 * Extracted from BaseCanvas.tsx to reduce component complexity
 */

import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import {
	playerHasPreSnapMovement as checkPlayerHasPreSnapMovement,
	validatePreSnapMovement,
} from '@/utils/PresnapValidation.utils'
import type { ControlPoint, Drawing } from '@/types'
import type { BaseCanvasCallbacks, Player, PreSnapMovement } from '../BaseCanvas.types'

interface PlacementMode {
	type: 'presnap'
	drawingId: string
}

interface EditingDrawingState {
	drawing: Drawing
	position: { x: number; y: number }
}

interface UsePreSnapMotionOptions {
	drawings: Drawing[]
	players: Player[]
	callbacks: BaseCanvasCallbacks
	editingDrawing: EditingDrawingState | null
	onBeforeStateChange?: () => void
}

interface UsePreSnapMotionReturn {
	placementMode: PlacementMode | null
	setPlacementMode: (mode: PlacementMode | null) => void
	playerHasPreSnapMovement: (playerId: string, excludeDrawingId?: string) => boolean
	handlePreSnapMovement: () => void
	handlePreSnapPlacementClick: (
		drawingId: string,
		clickType: 'terminal' | 'path',
		point: { x: number; y: number },
		pointId?: string,
	) => void
}

/**
 * Manages pre-snap motion operations: add/remove motion, placement mode, shift/motion creation
 */
export function usePreSnapMotion({
									 drawings,
									 players,
									 callbacks,
									 editingDrawing,
									 onBeforeStateChange,
								 }: UsePreSnapMotionOptions): UsePreSnapMotionReturn {
	const [placementMode, setPlacementMode] = useState<PlacementMode | null>(null)

	// Check if a player already has pre-snap movement on another drawing
	const playerHasPreSnapMovement = useCallback(
		(playerId: string, excludeDrawingId?: string): boolean => {
			return checkPlayerHasPreSnapMovement(playerId, drawings, excludeDrawingId)
		},
		[drawings],
	)

	// Handle Add/Remove Pre-Snap Movement button click
	const handlePreSnapMovement = useCallback(() => {
		if (!editingDrawing) return

		onBeforeStateChange?.()

		const drawing = editingDrawing.drawing

		// If already has pre-snap motion, remove it
		if (drawing.preSnapMotion) {
			// Build the updated drawing
			let updatedDrawing = { ...drawing, preSnapMotion: undefined }

			// If it was a motion, also remove the snap point from points
			if (drawing.preSnapMotion.type === 'motion' && drawing.preSnapMotion.snapPointId) {
				const { [drawing.preSnapMotion.snapPointId]: _removed, ...remainingPoints } =
					drawing.points
				updatedDrawing = { ...updatedDrawing, points: remainingPoints }
			}

			const updatedDrawings = drawings.map((d) =>
				d.id === drawing.id ? updatedDrawing : d,
			)
			callbacks.onDrawingsChange(updatedDrawings)

			// If it was a shift, remove the ghost player
			if (drawing.preSnapMotion.type === 'shift' && drawing.playerId) {
				const updatedPlayers = players.filter(
					(p) => !(p.isGhost && p.sourcePlayerId === drawing.playerId),
				)
				callbacks.onPlayersChange(updatedPlayers)
			}

			return
		}

		// Enter placement mode to add pre-snap movement
		setPlacementMode({
			type: 'presnap',
			drawingId: drawing.id,
		})
	}, [editingDrawing, drawings, players, callbacks, onBeforeStateChange])

	// Handle placement mode clicks on the drawing
	const handlePreSnapPlacementClick = useCallback(
		async (
			drawingId: string,
			clickType: 'terminal' | 'path',
			point: { x: number; y: number },
			_pointId?: string,
		) => {
			const drawing = drawings.find((d) => d.id === drawingId)
			if (!drawing || !drawing.playerId) return

			// Validate before applying pre-snap movement
			const validationError = validatePreSnapMovement(drawing, drawings, clickType)
			if (validationError) {
				toast.error(validationError)
				setPlacementMode(null)
				return
			}

			onBeforeStateChange?.()

			if (clickType === 'terminal') {
				// Create Shift - ghost player at terminal position
				const sourcePlayer = players.find((p) => p.id === drawing.playerId)
				if (!sourcePlayer) return

				const ghostPlayer: Player = {
					id: crypto.randomUUID(),
					x: point.x,
					y: point.y,
					label: sourcePlayer.label,
					color: sourcePlayer.color,
					isGhost: true,
					sourcePlayerId: drawing.playerId,
				}

				await callbacks.onPlayerAdd(ghostPlayer)

				// Update drawing with shift pre-snap motion
				const preSnap: PreSnapMovement = { type: 'shift' }
				callbacks.onPreSnapUpdate(drawingId, preSnap)
			} else {
				// Create Motion - snap point at clicked location
				// Insert a new control point at the clicked location
				const snapPointId = crypto.randomUUID()
				const newPoint: ControlPoint = {
					id: snapPointId,
					x: point.x,
					y: point.y,
					type: 'snap',
				}

				// Add the snap point to the drawing's point pool
				const updatedDrawing = {
					...drawing,
					points: {
						...drawing.points,
						[snapPointId]: newPoint,
					},
					preSnapMotion: {
						type: 'motion' as const,
						snapPointId,
					},
				}

				const updatedDrawings = drawings.map((d) =>
					d.id === drawingId ? updatedDrawing : d,
				)
				callbacks.onDrawingsChange(updatedDrawings)
			}

			// Exit placement mode
			setPlacementMode(null)
		},
		[drawings, players, callbacks, onBeforeStateChange],
	)

	return {
		placementMode,
		setPlacementMode,
		playerHasPreSnapMovement,
		handlePreSnapMovement,
		handlePreSnapPlacementClick,
	}
}
